"use client";

import { type ReactNode, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  RealtimeKitProvider,
  useRealtimeKitClient,
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { useEcho } from "@laravel/echo-react";

import LessonEventsHandler from "../components/LessonEventsHandler";
import PictureInPicture from "../components/PictureInPicture";
import { DuplicateSessionDialog } from "../components/DuplicateSessionDialog.tsx";
import { useClassroomStore } from "../store/useClassroomStore";

// Remove this import
// import { useAllParticipants } from "../hooks/useAllParticipants";
import useUserStore from "@/features/auth/store/useUserStore";
import AppLoader from "@/components/AppLoader.tsx";
import { useHelpPopupStore } from "@/features/help/store/useHelpPopupStore.tsx";
import { STUDENT_WORKSPACE_PATHS, TUTOR_WORKSPACE_PATHS } from "@/routes/paths";

interface PipModeProviderProps {
  children: ReactNode;
}

// Meeting wrapper component that handles Cloudflare Realtime Kit events
function MeetingWrapper({ children }: { children: ReactNode }) {
  const { lessonId } = useParams();
  const { user } = useUserStore();

  // Use Cloudflare Realtime Kit hooks
  const { meeting } = useRealtimeKitMeeting();

  // Get meeting state using Cloudflare selectors
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);

  // Get participants directly from Cloudflare selectors
  const self = useRealtimeKitSelector((m) => m.self);
  const remoteParticipants = useRealtimeKitSelector((m) => m.participants.joined.toArray());
  const allParticipants = self ? [self, ...remoteParticipants] : remoteParticipants;

  const presenterId = useRealtimeKitSelector((m) => {
    // Find the participant who is screen sharing - check self first, then others
    if (m.self?.screenShareEnabled) {
      return m.self.id;
    }

    const screenSharingParticipant = Array.from(m.participants.joined.values()).find(
      (p) => p.screenShareEnabled,
    );

    return screenSharingParticipant?.id || null;
  });

  // Get native Cloudflare states
  const audioEnabled = useRealtimeKitSelector((m) => m.self.audioEnabled);
  const videoEnabled = useRealtimeKitSelector((m) => m.self.videoEnabled);

  // Handle room state changes
  useEffect(() => {
    const store = useClassroomStore.getState();

    switch (roomState) {
      case "init":
        // Show prejoin UI
        store.setView("prejoin");
        store.setIsJoined(false);
        store.setIsLoading(false);
        break;
      case "joined":
        // Show call UI
        store.setView("call");
        store.setIsJoined(true);
        store.setShouldMaintainConnection(true);
        store.setMeetingInitialized(true);
        store.setIsLoading(false);
        break;
      case "left":
      case "ended":
      case "disconnected":
      case "kicked":
      case "rejected":
        // Reset state
        store.setIsJoined(false);
        store.setShouldMaintainConnection(false);
        store.clearMeetingData();
        break;
      default:
        break;
    }
  }, [roomState]);

  // Handle presenter changes (screen sharing)
  useEffect(() => {
    console.log("Cloudflare onPresenterChanged event:", presenterId);
    const store = useClassroomStore.getState();

    store.setPresenterId(presenterId);

    // Debug: Log participant states when screen sharing changes
    if (presenterId) {
      console.log("Screen sharing started by participant:", presenterId);
    } else {
      console.log("Screen sharing stopped");
    }

    // Auto switch to screenshare view when someone starts sharing
    if (presenterId && !store.isPipMode) {
      console.log("Switching to screenshare view");
      setTimeout(() => {
        const currentState = useClassroomStore.getState();

        // Double check we're still not in PiP mode and presenter is still active
        if (!currentState.isPipMode && currentState.presenterId === presenterId) {
          currentState.setView("screenshare");
          console.log("Switched to screenshare view");
        }
      }, 100);
    } else if (!presenterId) {
      // When screen sharing stops, switch back to call view
      console.log("Switching back to call view");
      setTimeout(() => {
        const currentState = useClassroomStore.getState();

        if (!currentState.isPipMode && currentState.view === "screenshare") {
          currentState.setView("call");
          console.log("Switched back to call view");
        }
      }, 100);
    }
  }, [presenterId]);

  // Video sync whisper event listeners
  const { channel } = useEcho(
    `lesson.${lessonId}`,
    [], // No regular events, only whispers
    () => {},
    [lessonId],
  );

  // Handle participant joined events
  useEffect(() => {
    if (!channel || !lessonId) return;

    // Only tutors should send sync data to new participants
    if (user?.role === "tutor" && channel) {
      const store = useClassroomStore.getState();

      // If there's an active video sync, send it to the new participant
      if (store.videoSync) {
        console.log("Sending video sync to new participant");

        // Send the current video state via whisper after a small delay to ensure participant is ready
        setTimeout(() => {
          if (channel()) {
            channel().whisper("videoSync", {
              videoId: store.videoSync!.videoId,
              time: store.videoSync!.time,
              isPlaying: store.videoSync!.isPlaying,
              targetParticipantId: "", // Optional: could be used for targeted sync
            });
          }
        }, 1000);
      }
    }
  }, [allParticipants, channel, lessonId, user?.role]);

  // Set up video sync whisper listeners
  useEffect(() => {
    if (!channel || !lessonId) return;

    // Listen for video sync updates
    channel().listenForWhisper("videoSync", (data: any) => {
      console.log("Received video sync whisper:", data);

      const store = useClassroomStore.getState();

      // Both tutors and students should update the store with received sync data
      // This ensures the video state is always up-to-date regardless of current view
      store.setVideoSync({
        videoId: data.videoId,
        time: data.time,
        isPlaying: data.isPlaying,
        lastUpdate: Date.now(),
      });
    });

    // Listen for video set events
    channel().listenForWhisper("videoSet", (data: any) => {
      console.log("Received video set whisper:", data);

      const store = useClassroomStore.getState();

      // Update video sync state with new video
      store.setVideoSync({
        videoId: data.videoId,
        time: data.time || 0,
        isPlaying: data.isPlaying || false,
        lastUpdate: Date.now(),
      });

      // All users (both tutors and students) should automatically switch to video view
      console.log("All users switching to video view");
      store.setView("video");
    });

    // Listen for video removed events
    channel().listenForWhisper("videoRemoved", () => {
      console.log("Received video removed whisper");

      const store = useClassroomStore.getState();

      // Clear video sync
      store.setVideoSync(null);

      // All users should switch back to call view
      console.log("All users switching back to call view");
      store.setView("call");
    });

    return () => {
      channel().stopListeningForWhisper("videoSync");
      channel().stopListeningForWhisper("videoSet");
      channel().stopListeningForWhisper("videoRemoved");
    };
  }, [channel, lessonId, user?.id, user?.role]);

  // Store leave function in window for global access
  useEffect(() => {
    if (meeting) {
      // @ts-ignore
      window.leaveMeeting = () => meeting.leave();
      // @ts-ignore
      window.joinMeeting = () => meeting.join();

      return () => {
        // @ts-ignore
        delete window.leaveMeeting;
        // @ts-ignore
        delete window.joinMeeting;
      };
    }
  }, [meeting]);

  // Sync Cloudflare native states to store for compatibility
  useEffect(() => {
    const store = useClassroomStore.getState();

    store.setMicOn(audioEnabled);
    store.setWebcamOn(videoEnabled);
  }, [audioEnabled, videoEnabled]);

  // Handle Cloudflare meeting events for cleanup
  useEffect(() => {
    if (!meeting) return;

    const handleRoomLeft = ({ state }: { state: string }) => {
      console.log("🚪 Cloudflare room left event:", state);
      const store = useClassroomStore.getState();

      // Handle different room left states
      switch (state) {
        case "left":
          console.log("👤 User has left the room");
          // Only clear if not maintaining connection
          if (!store.shouldMaintainConnection) {
            console.log("🧹 Clearing meeting data - user left room");
            store.setMeetingId(null);
            store.setVideoRoomData(null);
            store.setToken(null);
            store.setIsInitialized(false);
          }
          break;
        case "kicked":
          console.log("🚫 User was kicked from the room");
          store.setMeetingId(null);
          store.setVideoRoomData(null);
          store.setToken(null);
          store.setIsInitialized(false);
          break;
        case "ended":
          console.log("🏁 Meeting has ended");
          store.setMeetingId(null);
          store.setVideoRoomData(null);
          store.setToken(null);
          store.setIsInitialized(false);
          store.setLessonEnded(true);
          break;
        case "rejected":
          console.log("❌ User's request to join was rejected");
          store.setMeetingId(null);
          store.setVideoRoomData(null);
          store.setToken(null);
          store.setIsInitialized(false);
          break;
        case "disconnected":
          console.log("📡 Temporary network disconnection");
          // Don't clear data for temporary disconnection
          break;
        case "failed":
          console.log("💥 Failed to join the meeting");
          store.setMeetingId(null);
          store.setVideoRoomData(null);
          store.setToken(null);
          store.setIsInitialized(false);
          break;
        case "connected-meeting":
          console.log("🔄 User joined a connected meeting (breakout)");
          // Don't clear data for breakout meetings
          break;
        default:
          console.log("❓ Unknown room left state:", state);
          break;
      }
    };

    // Listen for room left events
    meeting.self.on("roomLeft", handleRoomLeft);

    return () => {
      // Clean up event listener
      meeting.self.off("roomLeft", handleRoomLeft);
    };
  }, [meeting]);

  return <>{children}</>;
}

// Component that creates the meeting and provides the RealtimeKitProvider
function MeetingProvider({ token, children }: { token: string; children: ReactNode }) {
  const [meeting, initMeeting] = useRealtimeKitClient();
  const isInitialized = useClassroomStore((state) => state.isInitialized);
  const videoRoomData = useClassroomStore((state) => state.videoRoomData);
  const redirect = useNavigate();
  const { lessonId, workspaceId } = useParams();
  const { user } = useUserStore();
  const { setContent, setIsOpen } = useHelpPopupStore();

  // Log when MeetingProvider mounts/unmounts
  useEffect(() => {
    console.log("🏗️ MeetingProvider mounted");

    return () => {
      console.log("🏗️ MeetingProvider unmounted");
    };
  }, []);

  // Initialize the meeting with the token only if not already initialized
  useEffect(() => {
    console.log("🔧 MeetingProvider initialization check:", {
      hasToken: !!token,
      hasMeeting: !!meeting,
      isInitialized,
    });

    if (token && !meeting && !isInitialized) {
      console.log("🚀 Initializing meeting with token");

      const initializeMeeting = async () => {
        try {
          await initMeeting({
            authToken: token,
          });
        } catch (error: any) {
          console.error("🚫 Meeting initialization failed:", error);

          const store = useClassroomStore.getState();

          // Extract error code from the error object
          let errorCode = "0001"; // Default to initialization failed

          if (error?.code) {
            errorCode = error.code.toString().padStart(4, "0");
          } else if (error?.message) {
            // Try to extract error code from message if available
            const codeMatch = error.message.match(/error code[:\s]*(\d+)/i);

            if (codeMatch) {
              errorCode = codeMatch[1].padStart(4, "0");
            }
          }

          store.setMeetingError({
            code: errorCode,
            message: error?.message || "Failed to initialize meeting",
          });
        }
      };

      initializeMeeting();
    } else if (token && meeting && !isInitialized) {
      console.log("⏳ Meeting exists but not initialized yet");
    } else if (token && meeting && isInitialized) {
      console.log("✅ Meeting already initialized");
    } else if (!token) {
      console.log("❌ No token available for initialization");
    }
  }, [token, meeting, isInitialized, initMeeting]);

  // Set isInitialized when meeting is ready
  useEffect(() => {
    const store = useClassroomStore.getState();

    if (meeting) {
      store.setIsInitialized(true);
      meeting.participants.joined.on("participantJoined", (participant) => {
        if (
          participant.customParticipantId === meeting.self.customParticipantId &&
          participant.id !== meeting.self.id &&
          meeting.self.roomState === "joined"
        ) {
          // Leave the meeting and clean up first
          meeting.leave();
          store.setShouldMaintainConnection(false);
          store.clearMeetingData();

          // Redirect to lesson details
          redirect(
            `${user?.role === "student" ? STUDENT_WORKSPACE_PATHS(workspaceId).LESSON_DETAILS : TUTOR_WORKSPACE_PATHS(workspaceId).LESSON_DETAILS}/${lessonId}/details`,
          );

          setContent(
            <DuplicateSessionDialog
              onClose={() => {
                setIsOpen(false);
              }}
            />,
          );
          setIsOpen(true);
        }
      });
    } else {
      store.setIsInitialized(false);
    }
  }, [meeting]);

  return (
    <>
      <RealtimeKitProvider fallback={<AppLoader />} value={meeting}>
        <MeetingWrapper>
          {children}
          <PictureInPicture />
          {videoRoomData && <LessonEventsHandler />}
        </MeetingWrapper>
      </RealtimeKitProvider>
    </>
  );
}

export default function PipModeProvider({ children }: PipModeProviderProps) {
  const location = useLocation();

  // Get token from store
  const token = useClassroomStore((state) => state.token);
  const isJoined = useClassroomStore((state) => state.isJoined);
  const isPipMode = useClassroomStore((state) => state.isPipMode);
  const lessonEnded = useClassroomStore((state) => state.lessonEnded);

  // Check if we're on a classroom path
  const isClassroomPath = /workspaces\/.*\/lesson\/.*/.test(location.pathname);

  // Handle PiP mode logic
  useEffect(() => {
    const state = useClassroomStore.getState();

    // If lesson has ended, disable PiP mode
    if (lessonEnded && isPipMode) {
      state.setIsPipMode(false);

      return;
    }

    // Determine if PiP should be active
    const shouldEnablePip = isJoined && !isClassroomPath && !lessonEnded;

    // Update PiP mode if needed
    if (isPipMode !== shouldEnablePip) {
      // If returning from PiP to classroom
      if (isPipMode && isClassroomPath && !shouldEnablePip) {
        state.setIsTransitioningFromPiP(true);
        state.setIsLoading(false);

        setTimeout(() => {
          state.setIsTransitioningFromPiP(false);
        }, 3000);
      }

      state.setIsPipMode(shouldEnablePip);
    }

    // Maintain connection when navigating to classroom
    if (isClassroomPath && isJoined) {
      state.setShouldMaintainConnection(true);
    }
  }, [location.pathname, isJoined, isClassroomPath, isPipMode, lessonEnded]);

  // Add beforeunload event listener to warn about page refresh
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if ((isJoined || isPipMode) && !lessonEnded) {
        event.preventDefault();
        event.returnValue = "";

        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isJoined, isPipMode, lessonEnded]);

  // Only render MeetingProvider when we have a token
  if (!token) {
    return <>{children}</>;
  }

  return <MeetingProvider token={token}>{children}</MeetingProvider>;
}
